import { type ComponentType, type ReactNode, useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';

import {
    Breakpoint,
    canUseIntersectionObserver,
    type SliderProps,
    useBreakpoint,
    useIntersection,
} from '@jsmdg/yoshi';
import { StaticSlideRow } from './StaticSlideRow';
import styles from './LazySlider.module.scss';

const LoadableYoshiSlider = loadable(async () => import('./YoshiSliderLoader'), {
    ssr: false,
}) as ComponentType<SliderProps & { readonly fallback?: ReactNode }>;

type LazySliderProps = SliderProps & {
    readonly freeScrollOnMobile?: boolean;
};

const LazySlider = ({ freeScrollOnMobile = false, ...props }: LazySliderProps): JSX.Element => {
    const isAboveMobile = useBreakpoint(Breakpoint.XS);
    const [isNearViewport, setTargetElement] = useIntersection({
        rootMargin: '300px',
        triggerOnce: true,
    });
    const [hasNoObserver, setHasNoObserver] = useState(false);
    const isNearOrUnobservable = isNearViewport || hasNoObserver;
    const isFreeScroll = freeScrollOnMobile && !isAboveMobile;
    const showSwiper = isNearOrUnobservable && !isFreeScroll;

    const observeRow = (node: HTMLDivElement | null): void => {
        if (!node) return;

        if (canUseIntersectionObserver()) {
            setTargetElement(node);
        } else {
            setHasNoObserver(true);
        }
    };

    const widestSlidesPerView = Math.ceil(
        Math.max(
            props.config.slidesPerView,
            ...Object.values(props.config.breakpoints ?? {}).map(
                breakpoint => breakpoint.slidesPerView ?? 0,
            ),
        ),
    );

    const staticRow = (
        <StaticSlideRow
            ref={isNearOrUnobservable ? undefined : observeRow}
            slides={
                isNearOrUnobservable && isFreeScroll
                    ? props.slides
                    : props.slides.slice(0, Math.max(1, widestSlidesPerView) + 1)
            }
            config={props.config}
            wrapperClassName={classNames(props.classes?.swiperWrapper, {
                [styles.freeScroll]: freeScrollOnMobile,
            })}
            slideClassName={props.classes?.swiperSlide}
            onTouchStart={props.handleTouchStart}
            onTouchMove={props.handleTouchMove}
        />
    );

    return showSwiper ? <LoadableYoshiSlider {...props} fallback={staticRow} /> : staticRow;
};

export { LazySlider };
